import { registerSW } from "virtual:pwa-register";

import { toast } from "sonner";

/** Register the service worker in auto-update mode. A new deploy installs,
 * skip-waits, and takes over so every client converges on the latest build
 * without a manual "reload" dance (prompt mode left stale clients stuck behind
 * a waiting worker). No-op where the SW isn't supported (e.g. some in-app
 * browsers).
 *
 * The critical piece: auto-update makes the NEW worker active, but the page
 * already in memory keeps running the OLD bundle until it reloads — so on an
 * installed iOS PWA (no refresh button; iOS suspends timers) every deploy left
 * the user a version behind ("live updates not working"). We reload exactly
 * once when a freshly-installed worker takes control, and check for an update
 * immediately on launch/resume (not just hourly), so a deploy converges within
 * seconds of opening the app. */
export function setupPWA(): void {
  if ("serviceWorker" in navigator) {
    // Only reload on a REAL update — i.e. when an existing controller is
    // replaced. The first-ever registration has no prior controller, so its
    // initial controllerchange must NOT trigger a reload (and the guard also
    // stops any reload loop after the refresh).
    const hadController = !!navigator.serviceWorker.controller;
    let reloading = false;
    navigator.serviceWorker.addEventListener("controllerchange", () => {
      if (reloading || !hadController) return;
      reloading = true;
      window.location.reload();
    });
  }
  registerSW({
    immediate: true,
    onOfflineReady() {
      toast.success("GraphView is ready to work offline");
    },
    onRegisteredSW(_swUrl, registration) {
      if (!registration) return;
      // Check right away (covers an iOS PWA resume, where the hourly timer was
      // suspended), then keep polling for long-lived desktop tabs.
      void registration.update();
      setInterval(() => void registration.update(), 60 * 60 * 1000);
    },
  });
}
